// Theatre Calls — the ward's side of the OT call slip.
//
// Theatre issues a call slip when it wants a patient brought down. That slip
// lived only inside the OT case screen, behind an OT permission a ward nurse
// does not have, so in practice theatre issued the slip and then telephoned the
// ward — which is exactly what the slip was meant to replace.
//
// TWO BUTTONS, because the ward has two answers:
//
//   Acknowledge  — seen; the patient is being got ready.
//   Patient sent — the patient has physically left. This performs the WARD
//                  CHECK-OUT, bed decision included: an inpatient's bed is held
//                  for their return, a daycare patient's is released. It used
//                  to be a separate action on a separate screen, and it was the
//                  one that got forgotten, leaving beds held for patients who
//                  were never coming back to them.
//
// The bed decision is printed on the row BEFORE the button is pressed. A nurse
// should know what will happen to the bed while they can still change it, not
// read about it in the confirmation.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import {
  acknowledgeTheatreCall,
  getTheatreCalls,
  listOf,
  sendPatientToTheatre,
} from "../api/nurseApi";
import { formatIstTime, istDayKey } from "../../../utils/istDateTime";

const BED_ACTIONS = [
  { value: "", label: "As the case type implies" },
  { value: "HELD", label: "Hold the bed — the patient is coming back" },
  { value: "RELEASED", label: "Release the bed — the patient is not coming back" },
];

const when = (dt) => {
  const day = istDayKey(dt);
  return day ? `${day} ${formatIstTime(dt)}` : "—";
};

/** What pressing "Patient sent" will do to the bed, in words. */
function bedLine(call) {
  if (call.bed_intent === "RELEASED") {
    return "Daycare — the bed will be released.";
  }
  return "Inpatient — the bed will be held for their return.";
}

/**
 * The confirmation, which exists ONLY because of the bed.
 *
 * Marking a patient as sent is not reversible from here and it changes a bed's
 * state, so the nurse gets one screen that says which bed and what will happen
 * to it, with the chance to override when the ward knows better than the case
 * type does.
 */
function SendDialog({ call, onClose, onSent }) {
  const [bedAction, setBedAction] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const res = await sendPatientToTheatre(call.id, {
        bed_action: bedAction || undefined,
        notes: notes.trim() || undefined,
      });
      if (res?.status !== "Success") {
        toast.error(res?.message || "Could not record the patient as sent");
        return;
      }
      toast.success(res.message || "Patient sent");
      // The check-out stands even when the bed did not move, so this is a
      // warning and not an error — but it must be seen.
      if (res.bed_warning) toast.warn(`Bed: ${res.bed_warning}`);
      onSent();
      onClose();
    } catch (err) {
      // Never axios's transport text ("Request failed with status code 500").
      toast.error(err?.response?.data?.message || "Could not record the patient as sent. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Send {call.patient_name} to theatre</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, mb: 2 }}>
          {call.procedure_name || "Procedure not named"} in {call.operating_room_name || "theatre"},
          scheduled {when(call.scheduled_start)}.
        </Typography>

        <Paper
          variant="outlined"
          sx={{ p: 1.5, mb: 2, bgcolor: "rgba(33,205,192,0.06)", borderColor: NURSE_COLORS.accent }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            Bed {call.bed_label || "—"} in {call.ward_name || "this ward"}
          </Typography>
          <Typography sx={{ fontSize: 13 }}>{bedLine(call)}</Typography>
        </Paper>

        <TextField
          select
          fullWidth
          size="small"
          label="Bed"
          value={bedAction}
          onChange={(e) => setBedAction(e.target.value)}
          sx={{ mb: 2 }}
          helperText="Change this only if the ward needs the bed regardless of the case type."
        >
          {BED_ACTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={send} disabled={busy}>
          {busy ? "Sending…" : "Patient has left the ward"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CallRow({ call, onChanged, onSend }) {
  const [busy, setBusy] = useState(false);
  const acknowledged = call.porter_task_status === "ACKNOWLEDGED";

  const acknowledge = async () => {
    setBusy(true);
    try {
      const res = await acknowledgeTheatreCall(call.id);
      if (res?.status !== "Success") {
        toast.error(res?.message || "Could not acknowledge");
        return;
      }
      toast.success(res.message || "Acknowledged");
      onChanged();
    } catch (err) {
      // Never axios's transport text ("Request failed with status code 500").
      toast.error(err?.response?.data?.message || "Could not acknowledge. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
        <Box sx={{ minWidth: 108 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
            {formatIstTime(call.scheduled_start) || "—"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            {istDayKey(call.scheduled_start) || ""}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography sx={{ fontSize: 14.5, fontWeight: 700 }}>
            {call.patient_name}
            {call.health_record_id ? ` · ${call.health_record_id}` : ""}
          </Typography>
          <Typography sx={{ fontSize: 13.5 }}>
            {call.procedure_name || "Procedure not named"}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
            {call.ward_name || "—"} / bed {call.bed_label || "—"} → {call.operating_room_name || "theatre"}
          </Typography>
          {call.special_instructions && (
            <Typography sx={{ fontSize: 12.5, mt: 0.5 }}>
              <strong>Instructions:</strong> {call.special_instructions}
            </Typography>
          )}
          {call.porter_note && (
            <Typography sx={{ fontSize: 12.5 }}>
              <strong>Note:</strong> {call.porter_note}
            </Typography>
          )}
          {/* Said before the button is pressed, not after. */}
          {!call.sent && (
            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
              {bedLine(call)}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          {call.sent ? (
            <Chip
              size="small"
              color="success"
              label={`Sent · bed ${String(call.ward_checkout?.bed_action || "").toLowerCase() || "—"}`}
            />
          ) : (
            <>
              <Button size="small" onClick={acknowledge} disabled={busy || acknowledged}>
                {acknowledged ? "Acknowledged" : "Acknowledge"}
              </Button>
              <Button size="small" variant="contained" onClick={() => onSend(call)}>
                Patient sent
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function TheatreCalls() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [includeSent, setIncludeSent] = useState(false);
  const [sending, setSending] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getTheatreCalls(includeSent ? { include_sent: 1 } : undefined);
      if (res?.status !== "Success") {
        setError(res?.message || "Could not load theatre calls");
        setCalls([]);
        return;
      }
      setCalls(listOf(res));
    } catch (err) {
      // The server's own reason when it sent one; never axios's transport text
      // ("Request failed with status code 500"), which means nothing to a nurse.
      setError(err?.response?.data?.message || "Could not load theatre calls. Please try again.");
      setCalls([]);
    } finally {
      setLoading(false);
    }
  }, [includeSent]);

  useEffect(() => {
    load();
  }, [load]);

  const pending = calls.filter((c) => !c.sent).length;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        sx={{ mb: 2, gap: 1 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Theatre Calls
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {pending === 0
              ? "No patient is waiting to go down."
              : `${pending} patient${pending === 1 ? "" : "s"} theatre is waiting for.`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={includeSent}
                onChange={(e) => setIncludeSent(e.target.checked)}
              />
            }
            label={<Typography sx={{ fontSize: 13 }}>Show already sent</Typography>}
          />
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={18} />
          <Typography sx={{ fontSize: 13.5 }}>Loading…</Typography>
        </Stack>
      ) : error ? (
        <Typography sx={{ color: "error.main", fontSize: 13.5 }}>{error}</Typography>
      ) : calls.length === 0 ? (
        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
          Nothing from theatre right now.
        </Typography>
      ) : (
        calls.map((c) => (
          <CallRow key={c.id} call={c} onChanged={load} onSend={setSending} />
        ))
      )}

      {sending && (
        <SendDialog call={sending} onClose={() => setSending(null)} onSent={load} />
      )}
    </Box>
  );
}
